import type { NexResponse } from '../types/invoice/nex';
import { vendorNamesMatchForProbableMatch } from './invoiceVendorMatch';

type InvoiceForVendorDisplay = import('../types/invoice').InvoiceDetailResponse;

const VENDOR_STOPWORDS = new Set(['the', 'of', 'and']);

/**
 * Match server-side `normalizeVendorComparableKey` for deduping in the UI.
 */
export function normalizeInvoiceVendorAliasKey(s: string): string {
    let normalized = s
        .trim()
        .toLowerCase()
        .normalize('NFKD')
        .replace(/\p{M}/gu, '');
    normalized = normalized.replace(/\s*&\s*/g, ' and ');
    normalized = normalized.replace(/[^\w\s]/g, ' ');
    const words = normalized
        .split(/\s+/)
        .map((w) => w.trim())
        .filter((w) => w.length > 0 && !VENDOR_STOPWORDS.has(w));
    return words.join('');
}

/** Display vendor from invoice `supplierText` (alternate names live in org invoice-text aliases). */
export function getInvoiceVendorDisplayName(invoice: {
    supplierText?: string | null;
}): string {
    return (invoice.supplierText ?? '').trim();
}

/**
 * NEX PO header vendor for a selected Nexsyis vendor name on this RO.
 */
export function findNexPoVendorMetaForName(
    nexResponse: NexResponse | null | undefined,
    vendorName: string
): { vendorId?: number; vendorName: string } | null {
    const target = vendorName.trim();
    if (!target || !Array.isArray(nexResponse)) return null;

    for (const po of nexResponse) {
        const poName = typeof po.vendorName === 'string' ? po.vendorName.trim() : '';
        if (!poName) continue;
        if (!vendorNamesMatchForProbableMatch(target, poName)) continue;
        const rawId = (po as { vendorID?: number; vendorId?: number }).vendorID ??
            (po as { vendorId?: number }).vendorId;
        const vendorId = rawId != null && Number.isFinite(rawId) ? rawId : undefined;
        return { vendorId, vendorName: poName };
    }

    const normalized = target.toLowerCase();
    for (const po of nexResponse) {
        const poName = typeof po.vendorName === 'string' ? po.vendorName.trim() : '';
        if (poName.toLowerCase() === normalized) {
            const rawId = (po as { vendorID?: number; vendorId?: number }).vendorID ??
                (po as { vendorId?: number }).vendorId;
            const vendorId = rawId != null && Number.isFinite(rawId) ? rawId : undefined;
            return { vendorId, vendorName: poName };
        }
    }

    return null;
}

/**
 * NEX vendor names available for this RO (PO headers + matched lines) and a default pick for alias UI.
 */
export function getNexVendorNamesForInvoiceAliasUi(
    invoice?: {
        nexResponse?: import('../types/invoice/nex').NexResponse | null;
        reconciliationIssues?: { matchedNexLines?: Array<{ nexLine?: { _poVendorName?: string } }> };
        supplierText?: string;
    } | null
): { options: string[]; suggestedNexVendor: string | null } {
    const inv = invoice ?? {};
    const fromPo = new Set<string>();
    if (Array.isArray(inv.nexResponse)) {
        for (const po of inv.nexResponse) {
            if (po && typeof po === 'object') {
                const v = (po as { vendorName?: unknown }).vendorName;
                if (typeof v === 'string' && v.trim()) fromPo.add(v.trim());
            }
        }
    }
    const fromLines = new Set<string>();
    for (const m of inv.reconciliationIssues?.matchedNexLines ?? []) {
        const v = m?.nexLine?._poVendorName;
        if (typeof v === 'string' && v.trim()) fromLines.add(v.trim());
    }
    const combined = Array.from(new Set([...Array.from(fromPo), ...Array.from(fromLines)])).sort((a, b) =>
        a.localeCompare(b)
    );
    let suggested: string | null = null;
    if (fromLines.size === 1) {
        suggested = Array.from(fromLines)[0];
    } else if (combined.length === 1) {
        suggested = combined[0];
    } else {
        const sup = (inv.supplierText ?? '').trim();
        if (sup && combined.includes(sup)) suggested = sup;
    }
    return { options: combined, suggestedNexVendor: suggested };
}

/** NEX PO vendor names from matched reconciliation lines. */
export function getMatchedNexPoVendorNamesFromInvoice(
    invoice: InvoiceForVendorDisplay | undefined
): string {
    const lines = invoice?.reconciliationIssues?.matchedNexLines;
    if (!Array.isArray(lines) || lines.length === 0) return '';
    const names = new Set<string>();
    for (const row of lines) {
        const v = String(row?.nexLine?._poVendorName ?? '').trim();
        if (v) names.add(v);
    }
    return Array.from(names).join(', ');
}

/** Resolved NEX vendor suggestions stored on a vendor-name mismatch issue. */
export function formatNexVendorMismatchSuggestions(vm: {
    nexVendorName?: string | null;
    nexVendorNames?: string[];
}): string {
    const single = vm.nexVendorName != null ? String(vm.nexVendorName).trim() : '';
    if (single !== '') return single;
    if (vm.nexVendorNames?.length) return vm.nexVendorNames.join(', ');
    return '';
}

/** When flagged-part NEX options are scoped to one vendor, use that name in Key Identifiers. */
export function inferNexVendorFromFlaggedIssues(invoice: InvoiceForVendorDisplay | undefined): string {
    const vendors = new Set<string>();
    for (const fp of invoice?.reconciliationIssues?.flaggedParts ?? []) {
        for (const sec of fp.issueDetails?.nexOptionsByVendor ?? []) {
            const v = String(sec.vendorName ?? '').trim();
            if (v) vendors.add(v);
        }
        const suggested = String(fp.issueDetails?.suggestedVendorName ?? '').trim();
        if (suggested) vendors.add(suggested);
    }
    if (vendors.size === 1) return Array.from(vendors)[0];
    return '';
}

/** Vendor label for Key Identifiers — prefer resolved NEX vendor over empty invoice extraction. */
export function getKeyIdentifiersVendorDisplay(params: {
    invoice: InvoiceForVendorDisplay | undefined;
    supplierText: string;
    vendorMismatch?: {
        invoiceVendorName?: string | null;
        nexVendorName?: string | null;
        nexVendorNames?: string[];
    };
}): string {
    const { invoice, supplierText, vendorMismatch } = params;
    const fromMatchedLines = getMatchedNexPoVendorNamesFromInvoice(invoice);
    const fromMismatch = vendorMismatch ? formatNexVendorMismatchSuggestions(vendorMismatch) : '';
    const fromFlaggedIssues = inferNexVendorFromFlaggedIssues(invoice);
    const { suggestedNexVendor, options } = getNexVendorNamesForInvoiceAliasUi(invoice);
    const fromSuggested =
        suggestedNexVendor ?? (options.length === 1 ? options[0] : '');

    return (
        fromMatchedLines ||
        fromMismatch ||
        fromFlaggedIssues ||
        fromSuggested ||
        supplierText.trim() ||
        (vendorMismatch?.invoiceVendorName ?? '').trim() ||
        '—'
    );
}
